import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import playIcon from "../../assets/Icons/play.png";
import { useGetTrooStories } from "../../hooks/useGetTrooStories";
import type { TrooStory } from "../../hooks/useGetTrooStories";
import { useUpdateUserPreferences } from "../../hooks/useUpdateUserPreferences";
import "./TrooStories.scss";
import { clearStoryData } from "../../srcWeb/store/storySlice";
import { useAppDispatch } from "../../srcWeb/store/hooks";
import { trackEvent } from "../../mixpanel";

const COMPLEXITY_LABELS: Record<string, string> = {
  Easy: "Ages 4-5",
  Medium: "Ages 6-7",
  Hard: "Ages 8-9",
  Difficult: "Ages 10-11",
};

interface TrooStoriesProps {
  schoolHash: string;
  teacherId: string;
  grade?: string;
  grades?: any[];
  onGradeChange?: (grade: string) => void;
}

const TrooStories: React.FC<TrooStoriesProps> = ({
  schoolHash,
  teacherId,
  grade = "",
  grades = [],
  onGradeChange,
}) => {
  const navigate = useNavigate();
  const { updateGrade } = useUpdateUserPreferences();
  const [selectedComplexity, setSelectedComplexity] = useState<string>("Easy");
  const [selectedCompetency, setSelectedCompetency] =
    useState<string>("All Competencies");
  const [pendingStory, setPendingStory] = useState<TrooStory | null>(null);
  // 'confirm' = left modal, 'class-select' = right modal, null = closed
  const [modalStep, setModalStep] = useState<"confirm" | "class-select" | null>(
    null,
  );
  const [modalGrade, setModalGrade] = useState<string>(grade);
  const [classSelectLoading, setClassSelectLoading] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setModalGrade(grade);
  }, [grade]);

  const { data, loading } = useGetTrooStories({
    schoolHash,
    teacherId,
    complexity: selectedComplexity,
    enabled: !!schoolHash && !!teacherId,
  });

  const competencies = data.filter(
    (c) => c.selCompetency.toLowerCase() !== "uncategorized",
  );

  const allStoriesSorted: TrooStory[] = competencies
    .flatMap((c) => c.stories)
    .sort((a, b) => a.order - b.order);

  const ALL_TAB = "All Competencies";

  const PINNED_LAST = [
    "Responsible Decision Making",
    "Relationship Building Skills",
  ];

  const orderedCompetencyNames = [
    ...competencies
      .map((c) => c.selCompetency)
      .filter((name) => !PINNED_LAST.includes(name)),
    ...PINNED_LAST.filter((name) =>
      competencies.some((c) => c.selCompetency === name),
    ),
  ];

  const tabs = [ALL_TAB, ...orderedCompetencyNames];

  useEffect(() => {
    setSelectedCompetency(ALL_TAB);
  }, [selectedComplexity]);

  const currentStories: TrooStory[] =
    selectedCompetency === ALL_TAB
      ? allStoriesSorted
      : competencies.find((c) => c.selCompetency === selectedCompetency)
          ?.stories || [];

  const openConfirmModal = (story: TrooStory) => {
    dispatch(clearStoryData());
    setPendingStory(story);
    setModalGrade(grade);
    setModalStep("confirm");
  };

  const navigateToStory = (story: TrooStory) => {
    const storyCompetency = competencies.find((c) =>
      c.stories.some((s) => s.storyId === story.storyId),
    )?.selCompetency;
    trackEvent("TROO_STORIES_OPENED", {
      age: story.ageGroup,
      competency: storyCompetency,
    });
    navigate("/web/WebStoryScreen", {
      state: {
        storyId: story.storyId === "-1" ? story.documentId : story.storyId,
        isDocumentId: story.storyId === "-1",
      },
    });
    setPendingStory(null);
    setModalStep(null);
  };

  const handleConfirmStart = () => {
    if (!pendingStory) return;
    navigateToStory(pendingStory);
  };

  const handleClassSelectConfirm = async () => {
    if (!pendingStory || classSelectLoading) return;
    // Sync grade state in parent immediately (updates header dropdown)
    // onGradeChange?.(modalGrade);
    // Wait for updateUserPreferences API to complete, then navigate
    setClassSelectLoading(true);
    try {
      await updateGrade(modalGrade);
    } finally {
      setClassSelectLoading(false);
      navigateToStory(pendingStory);
    }
  };

  const closeModal = () => {
    setPendingStory(null);
    setModalStep(null);
  };

  return (
    <div className="troo-stories">
      <div className="troo-stories__divider" />

      {/* Age / complexity filter */}
      <div className="troo-stories__age-filters">
        {Object.entries(COMPLEXITY_LABELS).map(([key, label]) => (
          <button
            key={key}
            className={`age-filter-pill${selectedComplexity === key ? " age-filter-pill--active" : ""}`}
            onClick={() => setSelectedComplexity(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* SEL competency tabs */}
      {competencies.length > 0 && (
        <div className="troo-stories__competency-tabs">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`competency-tab${selectedCompetency === tab ? " competency-tab--active" : ""}`}
              onClick={() => setSelectedCompetency(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="troo-stories__table">
        <div className="stories-table-header">
          <div className="col-name">Story name</div>
          <div className="col-themes">Themes</div>
          <div className="col-skills">Focus Skills</div>
          <div className="col-time">Estimated time</div>
          <div className="col-play">Play</div>
        </div>

        <div className="stories-table-body">
          {loading && (
            <div className="stories-table-message">Loading stories...</div>
          )}

          {!loading && currentStories.length === 0 && (
            <div className="stories-table-message">No stories available.</div>
          )}

          {!loading &&
            currentStories.map((story) => (
              <div key={story.documentId} className="stories-table-row">
                <div className="col-name">
                  <div className="story-info">
                    <span className="story-title">{story.name}</span>
                    {story.plot && (
                      <span className="story-plot">{story.plot}</span>
                    )}
                  </div>
                </div>

                <div className="col-themes">
                  {story.theme && (
                    <span className="tag tag--theme">{story.theme}</span>
                  )}
                </div>

                <div className="col-skills">
                  {story.focusSkills &&
                    story.focusSkills.split(",").map((skill, i) => (
                      <span key={i} className="tag tag--skill">
                        {skill.trim()}
                      </span>
                    ))}
                </div>

                <div className="col-time">
                  <span>{story.estimatedTime}</span>
                </div>

                <div className="col-play">
                  <div className="play-btn-wrap">
                    <img
                      src={playIcon}
                      alt="play"
                      className="play-btn"
                      onClick={() => openConfirmModal(story)}
                    />
                    {(story.status === "finished" ||
                      story.status === "completed" ||
                      story.status === "restarted") && (
                      <span className="play-btn-done">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M2.5 7L5.5 10L11.5 4"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* ── LEFT MODAL: confirm start ── */}
      {modalStep === "confirm" && pendingStory && (
        <div className="ts-modal-overlay" onClick={closeModal}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ts-modal__close" onClick={closeModal}>
              ×
            </button>
            <p className="ts-modal__title">Do you want to start this story?</p>
            <div className="ts-modal__info-box">
              <p className="ts-modal__info-line">
                Story Name:{" "}
                <span className="ts-modal__info-value">
                  {pendingStory.name}
                </span>
              </p>
              <p className="ts-modal__info-line">
                Classroom: <span className="ts-modal__info-value">{grade}</span>
              </p>
            </div>
            <div className="ts-modal__actions">
              <button
                className="ts-modal__btn ts-modal__btn--outline"
                onClick={() => setModalStep("class-select")}
              >
                No, let me change
              </button>
              <button
                className="ts-modal__btn ts-modal__btn--primary"
                onClick={handleConfirmStart}
              >
                ✓ Yes, start
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RIGHT MODAL: pick a different class ── */}
      {modalStep === "class-select" && pendingStory && (
        <div className="ts-modal-overlay" onClick={closeModal}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ts-modal__close" onClick={closeModal}>
              ×
            </button>
            <p className="ts-modal__title">
              Do you want to go to the Troo app for this class?
            </p>
            <div className="ts-modal__grade-pills">
              {grades.map((g: any) => (
                <button
                  key={g.classId}
                  className={`ts-modal__grade-pill${modalGrade === g.classId ? " ts-modal__grade-pill--active" : ""}`}
                  onClick={() => setModalGrade(g.classId)}
                >
                  {g.classId}
                </button>
              ))}
            </div>
            <div className="ts-modal__actions ts-modal__actions--class-select">
              <button
                className="ts-modal__btn ts-modal__btn--outline"
                onClick={() => setModalStep("confirm")}
              >
                Cancel
              </button>
              <button
                className="ts-modal__btn ts-modal__btn--primary"
                onClick={handleClassSelectConfirm}
                disabled={classSelectLoading}
              >
                {classSelectLoading ? "Please wait..." : "✓ Yes, go to the app"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrooStories;
